import React, { useEffect, useState } from "react";
import { Shell, Section, KpiCard } from "@/components/loomi/Layout";
import { ADMIN_NAV } from "./AdminDashboard";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, X, RefreshCw, Trash2, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";

const KIND_LABEL = {
  zamplia: "Zamplia", cint: "Cint", lucid: "Lucid", dynata: "Dynata", generic: "Generic",
};

export default function AdminPartners() {
  const [partners, setPartners] = useState([]);
  const [open, setOpen] = useState(false);
  const [busySync, setBusySync] = useState(null);

  const load = async () => { const { data } = await api.get("/admin/partners"); setPartners(data); };
  useEffect(() => { load(); }, []);

  const runSync = async (id, name) => {
    setBusySync(id);
    try {
      const { data } = await api.post(`/admin/partners/${id}/sync`);
      toast.success(`${name}: fetched ${data.fetched}, created ${data.created.length}, updated ${data.updated.length}`);
      load();
    } catch (e) { toast.error("Sync failed", { description: formatApiError(e) }); }
    finally { setBusySync(null); }
  };

  const remove = async (id, name) => {
    if (!window.confirm(`Remove partner "${name}"? Their sourced projects will be closed.`)) return;
    try { await api.delete(`/admin/partners/${id}`); toast.success("Partner removed"); load(); }
    catch (e) { toast.error("Failed", { description: formatApiError(e) }); }
  };

  return (
    <Shell title="Sample-exchange partners" portalLabel="Admin" nav={ADMIN_NAV}>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KpiCard label="Partners" value={partners.length} accent="primary" testId="partners-kpi-count" />
        <KpiCard label="Active" value={partners.filter(p => p.active).length} accent="green" testId="partners-kpi-active" />
        <KpiCard label="Last synced" value={
          partners.some(p => p.last_sync_at)
            ? new Date(Math.max(...partners.filter(p => p.last_sync_at).map(p => +new Date(p.last_sync_at)))).toLocaleTimeString()
            : "—"
        } accent="blue" testId="partners-kpi-lastsync" />
        <KpiCard label="Zamplia-like" value={partners.filter(p => ["zamplia","cint","lucid","dynata"].includes(p.kind)).length} accent="amber" testId="partners-kpi-known" />
      </div>

      <div className="mt-8">
        <Section title="Connected partners" testId="section-partners" action={
          <button onClick={() => setOpen(true)} data-testid="partners-new-btn" className="bg-indigo-500 hover:bg-indigo-400 text-xs font-medium px-3 py-2 rounded-md inline-flex items-center gap-1.5">
            <Plus className="w-3.5 h-3.5" /> Add partner
          </button>
        }>
          {partners.length === 0 ? (
            <div className="text-mute text-sm py-10 text-center" data-testid="partners-empty">
              <div className="text-mute mb-2">No partners connected.</div>
              <div className="text-xs">Connect Zamplia, Cint, Lucid, Dynata, or any REST-based sample-exchange platform.</div>
            </div>
          ) : (
            <table className="w-full text-sm" data-testid="partners-table">
              <thead>
                <tr className="text-left text-xs uppercase tracking-widest text-mute border-b border-white/10">
                  <th className="py-3 pr-4">Partner</th><th className="py-3 pr-4">Kind</th>
                  <th className="py-3 pr-4">Base URL</th><th className="py-3 pr-4">Last sync</th>
                  <th className="py-3 pr-4">Active</th><th className="py-3" />
                </tr>
              </thead>
              <tbody>
                {partners.map((p) => (
                  <tr key={p.id} className="border-b border-white/5 hover:bg-white/5" data-testid={`partner-row-${p.id}`}>
                    <td className="py-3 pr-4">
                      <Link to={`/admin/partners/${p.id}`} className="font-medium hover:underline" data-testid={`partner-link-${p.id}`}>{p.name}</Link>
                      <div className="text-[10px] text-mute font-mono">supplier_id: {p.supplier_id || "—"}</div>
                    </td>
                    <td className="py-3 pr-4"><span className="text-xs uppercase tracking-widest text-mute">{KIND_LABEL[p.kind] || p.kind}</span></td>
                    <td className="py-3 pr-4 font-mono text-[11px] text-mute max-w-[280px] truncate">{p.base_url}</td>
                    <td className="py-3 pr-4 text-xs text-mute">{p.last_sync_at ? new Date(p.last_sync_at).toLocaleString() : "never"}<div className="text-[10px]">{p.sync_status || ""}</div></td>
                    <td className="py-3 pr-4">
                      <span className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded-full border ${p.active ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" : "bg-white/5 text-mute border-white/10"}`}>{p.active ? "active" : "off"}</span>
                    </td>
                    <td className="py-3 text-right space-x-1 whitespace-nowrap">
                      <button onClick={() => runSync(p.id, p.name)} disabled={busySync === p.id} data-testid={`partner-sync-${p.id}`} className="text-xs bg-indigo-500/15 border border-indigo-500/30 text-indigo-300 rounded-md px-2 py-1 inline-flex items-center gap-1 disabled:opacity-60">
                        <RefreshCw className={`w-3 h-3 ${busySync === p.id ? "animate-spin" : ""}`} /> {busySync === p.id ? "Syncing…" : "Sync"}
                      </button>
                      <Link to={`/admin/partners/${p.id}`} className="text-xs text-indigo-300 hover:underline" data-testid={`partner-open-${p.id}`}>Open →</Link>
                      <button onClick={() => remove(p.id, p.name)} data-testid={`partner-delete-${p.id}`} className="text-xs bg-rose-500/15 border border-rose-500/30 text-rose-300 rounded-md px-2 py-1 inline-flex items-center gap-1">
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Section>
      </div>

      {open && <NewPartnerModal onClose={() => setOpen(false)} onCreated={() => { setOpen(false); load(); }} />}
    </Shell>
  );
}

function NewPartnerModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    name: "Zamplia", kind: "zamplia",
    base_url: "https://api.zamplia.com",
    api_key: "", supplier_id: "",
    feed_path: "/api/v1/opportunities",
    auth_scheme: "header", auth_header_name: "X-API-Key",
    entry_url_template: "", webhook_secret: "", active: true,
  });
  const [saving, setSaving] = useState(false);

  const applyPreset = (kind) => {
    const presets = {
      zamplia: { name: "Zamplia", base_url: "https://api.zamplia.com", feed_path: "/api/v1/opportunities", auth_scheme: "header", auth_header_name: "X-API-Key" },
      cint: { name: "Cint", base_url: "https://api.cint.com/v2", feed_path: "/programmatic/supply/projects", auth_scheme: "bearer", auth_header_name: "" },
      lucid: { name: "Lucid Marketplace", base_url: "https://api.samplicio.us", feed_path: "/Supply/v1/Projects/BySupplier", auth_scheme: "header", auth_header_name: "Authorization" },
      dynata: { name: "Dynata", base_url: "https://api.dynata.com", feed_path: "/v1/opportunities", auth_scheme: "bearer" },
      generic: { name: "", base_url: "", feed_path: "/api/v1/opportunities", auth_scheme: "header", auth_header_name: "X-API-Key" },
    };
    const p = presets[kind] || presets.generic;
    setForm({ ...form, kind, ...p });
  };

  const submit = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      await api.post("/admin/partners", form);
      toast.success("Partner connected");
      onCreated();
    } catch (err) { toast.error("Failed", { description: formatApiError(err) }); }
    finally { setSaving(false); }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={onClose} data-testid="new-partner-modal">
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="glass-strong rounded-2xl w-full max-w-2xl p-6 relative max-h-[90vh] overflow-y-auto">
        <button type="button" onClick={onClose} className="absolute top-4 right-4 text-mute hover:text-white" data-testid="new-partner-close"><X className="w-4 h-4" /></button>
        <h3 className="font-display font-extrabold text-2xl mb-1">Connect a sample-exchange partner</h3>
        <p className="text-mute text-sm mb-4">Configure how SyncLine authenticates and pulls opportunities from an external supply platform like Zamplia, Cint, Lucid, or Dynata.</p>

        <label className="block text-[10px] uppercase tracking-widest text-mute mb-1">Preset</label>
        <div className="flex gap-2 mb-4 flex-wrap">
          {["zamplia","cint","lucid","dynata","generic"].map((k) => (
            <button type="button" key={k} onClick={() => applyPreset(k)} data-testid={`preset-${k}`}
              className={`text-xs px-3 py-1.5 rounded-md border ${form.kind === k ? "bg-indigo-500/20 border-indigo-400/50 text-indigo-200" : "bg-white/5 border-white/10 text-mute hover:bg-white/10"}`}>
              {KIND_LABEL[k]}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-2 gap-3">
          <F label="Partner name" wide><input required data-testid="new-partner-name" value={form.name} onChange={set("name")} className="in" /></F>
          <F label="Base URL" wide><input required data-testid="new-partner-base-url" value={form.base_url} onChange={set("base_url")} placeholder="https://api.zamplia.com" className="in font-mono" /></F>
          <F label="API key" wide><input required type="password" data-testid="new-partner-api-key" value={form.api_key} onChange={set("api_key")} placeholder="secret from partner dashboard" className="in font-mono" /></F>
          <F label="Supplier ID"><input data-testid="new-partner-supplier-id" value={form.supplier_id} onChange={set("supplier_id")} placeholder="assigned by partner" className="in font-mono" /></F>
          <F label="Feed path"><input required data-testid="new-partner-feed-path" value={form.feed_path} onChange={set("feed_path")} placeholder="/api/v1/opportunities" className="in font-mono" /></F>
          <F label="Auth scheme">
            <select data-testid="new-partner-auth-scheme" value={form.auth_scheme} onChange={set("auth_scheme")} className="in">
              <option value="header">Custom header</option>
              <option value="bearer">Bearer token</option>
              <option value="query">Query param</option>
            </select>
          </F>
          <F label="Auth header name"><input data-testid="new-partner-auth-header" value={form.auth_header_name} onChange={set("auth_header_name")} placeholder="X-API-Key" className="in font-mono" /></F>
          <F label="Webhook signing secret (optional)" wide><input type="password" data-testid="new-partner-webhook-secret" value={form.webhook_secret} onChange={set("webhook_secret")} placeholder="for verifying inbound postbacks" className="in font-mono" /></F>
        </div>

        <div className="mt-5 flex justify-end gap-2">
          <button type="button" onClick={onClose} data-testid="new-partner-cancel" className="px-4 py-2 rounded-md text-sm bg-white/5 hover:bg-white/10 border border-white/10">Cancel</button>
          <button type="submit" disabled={saving} data-testid="new-partner-submit" className="px-4 py-2 rounded-md text-sm bg-indigo-500 hover:bg-indigo-400 disabled:opacity-60">{saving ? "Connecting…" : "Connect partner"}</button>
        </div>
        <style>{`.in{width:100%;background:rgba(0,0,0,0.5);border:1px solid rgba(255,255,255,0.1);border-radius:6px;padding:8px 12px;font-size:14px;color:#fff}
.in:focus{outline:none;border-color:#6366f1;box-shadow:0 0 0 1px #6366f1}`}</style>
      </form>
    </div>
  );
}

function F({ label, wide, children }) {
  return (<div className={wide ? "col-span-2" : ""}>
    <label className="block text-[10px] uppercase tracking-widest text-mute mb-1">{label}</label>
    {children}
  </div>);
}
